import {FREE_WIN_PLAYER_ID, MATCH_RESULTS} from "../../util/Constants";
import ReactCountryFlag from "react-country-flag";
import {formatDate, playerToString, signedNumber} from "../../util/StringUtils";
import {getLadderColor, getRatingColorVariant, isMatchFinished} from "../../util/HelperUtils";
import {useIsMobile} from "../../hooks/useIsMobile";
import {getLoggedUser, isAdmin} from "../../util/SecurityUtils";
// canManage flows in from the tournament detail (backend-computed: admin || creator || organizer).
// When it's not provided (e.g. profile view, hall of fame), we fall back to the previous rule
// of "admin can edit anything".
import {Link} from "react-router-dom";
import {matchesApi} from "../../api/matches";
import {useEffect, useState, useCallback, type CSSProperties, type MouseEvent as ReactMouseEvent} from "react";
import {ProgressBar} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import MatchDetailModal from "./MatchDetailModal";
import type {Match, Player, SetScore} from "../../types/domain";

type MatchBoxVariant = "default" | "unfinished" | "profile";

interface MatchBoxProps {
  match: Match;
  variant?: MatchBoxVariant;
  onClick?: () => void;
  indicateFinished?: boolean;
  showDetailButton?: boolean;
  ratingId?: number | null;
  hideStatus?: boolean;
  /** When set, the win/loss/in-progress left border is rendered from this player's
   *  perspective instead of the logged-in user's. */
  perspectivePlayerId?: number | null;
  /** Caller opts in to surfacing the backend's `riskOfDelay` flag as a red border. */
  highlightRiskOfDelay?: boolean;
  /** When true, the current viewer can manage the tournament (admin, creator, or organizer)
   *  and therefore can click to enter/edit a match result even if they aren't a participant. */
  canManage?: boolean;
}

// Internal row type — same as SetScore but scores may be "-" placeholder strings
// when results are absent (so the UI can render a dash without diverging code paths).
type DisplaySetScore = {
  resultOrder: number;
  playerOneScore: number | string;
  playerTwoScore: number | string;
};

/**
 * Unified match box component.
 *
 * Variants:
 *  - "default": completed/in-progress match inside a tournament view.
 *  - "unfinished": minimal box used in the current round before result is set.
 *  - "profile": match shown outside its tournament context (player profile, hall of fame).
 */
function MatchBox(props: MatchBoxProps) {
  const {
    match,
    variant = "default",
    onClick,
    indicateFinished,
    showDetailButton = true,
    ratingId,
    hideStatus,
    perspectivePlayerId,
    highlightRiskOfDelay,
    canManage,
  } = props;

  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  const [likes, setLikes] = useState<number>(match.likes);
  const [likedByUser, setLikedByUser] = useState<boolean>(match.likedByUser);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const isProfile = variant === "profile";
  const isUnfinished = variant === "unfinished";
  const showResults = !isUnfinished;
  const showLikes = !isUnfinished;
  const showRating = !isUnfinished && ratingId != null;

  useEffect(() => {
    setLikes(match.likes);
    setLikedByUser(match.likedByUser);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [match.likes]);

  const handleMatchClick = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('.modal')) return;
    if (onClick) onClick();
  }, [onClick]);

  const handleDetailClick = (e: ReactMouseEvent<HTMLSpanElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDetailModal(true);
  };

  const handleLike = async (e: ReactMouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (!getLoggedUser()?.id) return;
    try {
      if (likedByUser) {
        await matchesApi.unlike(match.id);
        setLikedByUser(false);
        setLikes(likes - 1);
      } else {
        await matchesApi.like(match.id);
        setLikedByUser(true);
        setLikes(likes + 1);
      }
    } catch (_) { /* swallow */ }
  };

  // ----- match result derivation -----

  let playerOneSetsWon = match.results.filter((r: SetScore) => r.playerOneScore > r.playerTwoScore).length;
  let playerTwoSetsWon = match.results.filter((r: SetScore) => r.playerTwoScore > r.playerOneScore).length;
  let isDefaultWin = false;
  if (match.state === MATCH_RESULTS.PLAYER_ONE_DEFAULT) {
    playerOneSetsWon = match.setsNeededToWin;
    isDefaultWin = true;
  } else if (match.state === MATCH_RESULTS.PLAYER_TWO_DEFAULT) {
    playerTwoSetsWon = match.setsNeededToWin;
    isDefaultWin = true;
  }
  const results: DisplaySetScore[] = [...match.results];
  if (results.length === 0) {
    for (let i = 0; i < match.setsNeededToWin; i++) {
      results.push({ resultOrder: i + 1, playerOneScore: "-", playerTwoScore: "-" });
    }
  }

  const getSetResultStyle = (myScore: number | string, theirScore: number | string): string =>
    myScore > theirScore ? "spacing set-win" : "spacing set-loss";

  const getResultStyle = (mySetsWon: number, theirSetsWon: number): string => {
    if (mySetsWon > theirSetsWon) return "win";
    if (theirSetsWon > mySetsWon) return "loss";
    return "tie";
  };

  // ----- player row -----

  const playerLinkStyle: CSSProperties = isProfile || isUnfinished
    ? { textDecoration: 'none' }
    : { textDecoration: 'none', color: 'var(--accent)' };

  const renderPlayerRow = (player: Player, eloBefore: number | null, eloAfter: number | null) => (
    <div className="player-row">
      <ReactCountryFlag countryCode={player.nationality ?? "UN"} svg title={player.nationality ?? "UN"} className="flag me-2" />
      <span className="player">
        <Link to={"/players/" + player.id} style={playerLinkStyle}>
          {isProfile ? playerToString(player) : playerToString(player, false, true)}
        </Link>
      </span>
      {eloAfter != null
        ? isUnfinished
          ? <>&nbsp;({eloBefore != null ? eloBefore : player.elo})</>
          : <>
              &nbsp;({eloBefore != null ? eloBefore : player.elo}
              <span className={getResultStyle(eloAfter, eloBefore ?? 0)}> {signedNumber(eloAfter - (eloBefore ?? 0), true)}</span>
              )
            </>
        : <span> ({player.elo})</span>}
    </div>
  );

  // ----- container classes & style -----

  const loggedUser = getLoggedUser();
  const isPlayersMatch = match.playerOne.id === loggedUser?.playerId || match.playerTwo.id === loggedUser?.playerId;
  // Prefer the tournament-computed canManage flag when the caller provided it (covers admin +
   // creator + organizer). Fall back to isAdmin() for callers that don't have a tournament
   // context handy (profile pages, standalone match detail, etc).
  const isUserAuthorizedToEditMatch = (canManage ?? isAdmin()) || isPlayersMatch;
  const isFreeWinMatch = match.playerOne.id === FREE_WIN_PLAYER_ID || match.playerTwo.id === FREE_WIN_PLAYER_ID;
  const isClickable = !isProfile && isUserAuthorizedToEditMatch && !isFreeWinMatch;

  let matchBoxStyle = "matchbox";
  if (isProfile) matchBoxStyle += " profile-matchbox position-relative";
  if (isClickable && onClick && (isUnfinished || indicateFinished)) matchBoxStyle += " clickable";
  if (!isProfile && !isUnfinished && isMatchFinished(match.state) && indicateFinished) matchBoxStyle += " finished";
  if (highlightRiskOfDelay && match.riskOfDelay && !isProfile && !isMatchFinished(match.state)) matchBoxStyle += " risk-of-delay";

  const perspectiveId = perspectivePlayerId ?? loggedUser?.playerId ?? null;
  const isPerspectivePlayerMatch = perspectiveId != null &&
    (match.playerOne.id === perspectiveId || match.playerTwo.id === perspectiveId);
  if (!isProfile && isPerspectivePlayerMatch) {
    if (isUnfinished || match.state === MATCH_RESULTS.IN_PROGRESS) {
      matchBoxStyle += " my-match-in-progress";
    } else if (match.state === MATCH_RESULTS.PLAYER_ONE_WON || match.state === MATCH_RESULTS.PLAYER_ONE_DEFAULT) {
      matchBoxStyle += match.playerOne.id === perspectiveId ? " my-match-win" : " my-match-loss";
    } else if (match.state === MATCH_RESULTS.PLAYER_TWO_WON || match.state === MATCH_RESULTS.PLAYER_TWO_DEFAULT) {
      matchBoxStyle += match.playerTwo.id === perspectiveId ? " my-match-win" : " my-match-loss";
    }
  }

  const profileBorderStyle: CSSProperties = isProfile
    ? (hideStatus ? { border: "none" } : { borderLeftColor: match.isWin ? "green" : "red" })
    : { position: "relative" };

  const pinecRating = showRating
    ? ((match.playerOne.id === ratingId ? match.playerOneRating : match.playerTwoRating) ?? 0)
    : 0;

  // ----- detail button positioning -----

  const detailButtonBottom = isProfile && showRating ? "10px" : "8px";
  const detailButtonAccentClass = isProfile ? undefined : 'var(--accent)';

  // ----- render -----

  return (
    <div className="matchbox-hover-area">
      <div className={matchBoxStyle} onClick={isClickable && onClick ? handleMatchClick : undefined} style={profileBorderStyle}>

        {showLikes && !isDefaultWin && (
          <div className="like-container">
            <div className="me-2">{likes}</div>
            <div
              className={likedByUser ? "liked-heart" : (getLoggedUser()?.id ? "unliked-heart" : "empty-heart")}
              onClick={handleLike}
            />
          </div>
        )}

        {isProfile && (
          <div className="mb-2" style={{borderBottom: "1px solid", borderColor: "lightgray"}}>
            {isMobile ? (
              <>
                <div>
                  <Link to={"/tournaments/" + match.tournament.id} style={{ textDecoration: 'none', color: "lightgreen" }}>
                    {match.tournament.name}
                  </Link>
                </div>
                <div style={{fontSize: "0.9em"}}>
                  <span>{formatDate(match.createdAt)} - </span>
                  <span style={{color: getLadderColor(match.ladder.id)}}>
                    {i18n.language === "cs" ? match.ladder.name : match.ladder.nameEn}
                  </span>
                </div>
              </>
            ) : (
              <>
                <Link to={"/tournaments/" + match.tournament.id} style={{ textDecoration: 'none', color: "lightgreen" }}>
                  {match.tournament.name}
                </Link>
                <span> - {formatDate(match.createdAt)} - </span>
                <span style={{color: getLadderColor(match.ladder.id)}}>
                  {i18n.language === "cs" ? match.ladder.name : match.ladder.nameEn}
                </span>
              </>
            )}
          </div>
        )}

        <div className="players-and-results">
          <div className="player-rows">
            {renderPlayerRow(match.playerOne, match.playerOneEloBeforeMatchFinished, match.playerOneEloAfterMatchFinished)}
            {renderPlayerRow(match.playerTwo, match.playerTwoEloBeforeMatchFinished, match.playerTwoEloAfterMatchFinished)}
          </div>
          {showResults && (
            <div className="results">
              <div className="result">
                <div className={getResultStyle(playerOneSetsWon, playerTwoSetsWon)}>{playerOneSetsWon}</div>
                {results.map(r => (
                  <div key={"result-p1-" + match.id + r.resultOrder} className={getSetResultStyle(r.playerOneScore, r.playerTwoScore)}>
                    {r.playerOneScore}
                  </div>
                ))}
              </div>
              <div className="result">
                <div className={getResultStyle(playerTwoSetsWon, playerOneSetsWon)}>{playerTwoSetsWon}</div>
                {results.map(r => (
                  <div key={"result-p2-" + match.id + r.resultOrder} className={getSetResultStyle(r.playerTwoScore, r.playerOneScore)}>
                    {r.playerTwoScore}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {showResults && (
          <div className="inline-results">
            <span className={getResultStyle(playerOneSetsWon, playerTwoSetsWon)}>{playerOneSetsWon}</span>
            &nbsp;-&nbsp;
            <span className={getResultStyle(playerTwoSetsWon, playerOneSetsWon)}>{playerTwoSetsWon}</span>
            {isProfile ? <>&nbsp;&nbsp;</> : <>&nbsp;&nbsp;&nbsp;&nbsp;</>}
            {results.map((r, index) =>
              <div key={"inline-results-" + match.id + r.resultOrder} className="d-inline-block mt-1">
                <span className={getSetResultStyle(r.playerOneScore, r.playerTwoScore)}>{r.playerOneScore} </span>
                :
                <span className={getSetResultStyle(r.playerTwoScore, r.playerOneScore)}> {r.playerTwoScore}</span>
                {index !== results.length - 1 ? <span>&nbsp;&nbsp;•&nbsp;&nbsp;</span> : null}
              </div>
            )}
          </div>
        )}

        {showRating && (
          <div className="mt-2 d-flex gap-2 justify-content-start">
            <div>Rating:</div>
            <div className={(isProfile && isMobile ? "me-5" : "me-2") + " ms-2 flex-grow-1"} style={{maxWidth: "350px"}}>
              <ProgressBar
                label={pinecRating.toFixed(2)}
                now={pinecRating}
                key={1}
                max={2.0}
                min={-0.1}
                variant={getRatingColorVariant(pinecRating)}
                animated={pinecRating > 2.0}
              />
            </div>
          </div>
        )}

        {showDetailButton && !isFreeWinMatch && (
          <span
            onClick={handleDetailClick}
            style={{
              position: "absolute",
              bottom: detailButtonBottom,
              right: "8px",
              color: detailButtonAccentClass || 'var(--accent)',
              cursor: 'pointer',
              fontSize: '1em',
            }}
          >
            {t("detail")}
          </span>
        )}

        <MatchDetailModal
          isOpen={showDetailModal}
          matchId={match.id}
          onClose={() => setShowDetailModal(false)}
        />
      </div>
    </div>
  );
}

export default MatchBox;
